if (!customElements.get('faqs-categories')) {
  class FaqsCategories extends HTMLElement {
    static #TRANSITION_FALLBACK_MS = 350;
    static #SCROLL_SETTLE_MS = 120;

    #categoryObserver = null;
    #handleResize = null;
    #handleScrollEnd = null;
    #scrollEndTimer = null;
    #isObserverBound = false;
    #lockedCategoryId = null;
    #sections = [];
    #links = [];

    #handleInputChange = (ev) => {
      const targetId = ev.currentTarget.value;
      const elTarget = document.getElementById(targetId);
      const elSelect = ev.currentTarget.closest('custom-select');

      if (!elTarget) return;

      this.#lockActiveCategory(targetId);

      if (elSelect?.getAttribute('open') === 'true') {
        elSelect.setAttribute('open', 'false');
        this.#scrollAfterSelectClose(elTarget, elSelect);
        return;
      }

      elTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    #handleLinkClick = (ev) => {
      ev.preventDefault();

      const link = ev.currentTarget;
      const targetId = link.dataset.faqCategory;
      const elTarget = document.getElementById(targetId);

      if (!elTarget) return;

      this.#lockActiveCategory(targetId);
      elTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      history.replaceState(null, '', `#${targetId}`);
    };

    #scrollAfterSelectClose(elTarget, elSelect) {
      let scrolled = false;

      const scroll = () => {
        if (scrolled) return;
        scrolled = true;
        elTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      };

      const onTransitionEnd = (ev) => {
        if (ev.target !== elSelect || ev.propertyName !== 'grid-template-rows') return;
        elSelect.removeEventListener('transitionend', onTransitionEnd);
        window.clearTimeout(fallbackId);
        scroll();
      };

      elSelect.addEventListener('transitionend', onTransitionEnd);

      const fallbackId = window.setTimeout(() => {
        elSelect.removeEventListener('transitionend', onTransitionEnd);
        scroll();
      }, FaqsCategories.#TRANSITION_FALLBACK_MS);
    }

    connectedCallback() {
      this.#bindActiveCategoryObserver();
      this.#bindSelectWhenReady();
    }

    disconnectedCallback() {
      this.#categoryObserver?.disconnect();
      this.#categoryObserver = null;
      this.#isObserverBound = false;

      if (this.#handleResize) {
        window.removeEventListener('resize', this.#handleResize);
        this.#handleResize = null;
      }

      if (this.#handleScrollEnd) {
        window.removeEventListener('scroll', this.#handleScrollEnd);
        this.#handleScrollEnd = null;
      }

      window.clearTimeout(this.#scrollEndTimer);
    }

    async #bindSelectWhenReady() {
      if (customElements.get('custom-select')) {
        this.#bindSelect();
        return;
      }

      await customElements.whenDefined('custom-select');
      this.#bindSelect();
    }

    #bindSelect() {
      const elSelect = this.querySelector('custom-select');
      if (!elSelect) return;

      for (const elInput of elSelect.querySelectorAll('input[type="radio"]')) {
        elInput.addEventListener('change', this.#handleInputChange);
      }
    }

    #getScrollOffset() {
      const headerHeight = getComputedStyle(document.documentElement)
        .getPropertyValue('--header-height')
        .trim();

      if (!headerHeight) return 0;

      const elProbe = document.createElement('div');
      elProbe.style.cssText = `position:absolute;visibility:hidden;top:0;height:${headerHeight}`;
      document.body.appendChild(elProbe);
      const offset = elProbe.getBoundingClientRect().height;
      elProbe.remove();

      return offset;
    }

    #lockActiveCategory(activeId) {
      this.#lockedCategoryId = activeId;
      this.#setActiveCategory(activeId);

      if (this.#handleScrollEnd) {
        window.removeEventListener('scroll', this.#handleScrollEnd);
      }

      this.#handleScrollEnd = () => {
        window.clearTimeout(this.#scrollEndTimer);
        this.#scrollEndTimer = window.setTimeout(() => {
          window.removeEventListener('scroll', this.#handleScrollEnd);
          this.#handleScrollEnd = null;
          this.#lockedCategoryId = null;
          this.#updateActiveCategory();
        }, FaqsCategories.#SCROLL_SETTLE_MS);
      };

      window.addEventListener('scroll', this.#handleScrollEnd, { passive: true });
      this.#handleScrollEnd();
    }

    #setActiveCategory(activeId) {
      if (!activeId) return;

      for (const link of this.#links) {
        const isActive = link.dataset.faqCategory === activeId;
        link.classList.toggle('active', isActive);

        if (isActive) {
          link.setAttribute('aria-current', 'true');
        } else {
          link.removeAttribute('aria-current');
        }
      }
    }

    #updateActiveCategory() {
      if (!this.#sections.length || !this.#links.length) return;

      if (this.#lockedCategoryId) {
        this.#setActiveCategory(this.#lockedCategoryId);
        return;
      }

      const offset = this.#getScrollOffset();
      const isAtBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;

      if (isAtBottom) {
        this.#setActiveCategory(this.#sections[this.#sections.length - 1].dataset.faqCategory);
        return;
      }

      let activeSection = this.#sections[0];

      for (const section of this.#sections) {
        const elHeading = section.querySelector('.faqs-template--category-heading');
        if (!elHeading) continue;

        if (elHeading.getBoundingClientRect().top <= offset + 1) {
          activeSection = section;
        }
      }

      this.#setActiveCategory(activeSection.dataset.faqCategory);
    }

    #createCategoryObserver() {
      const offset = this.#getScrollOffset();

      return new IntersectionObserver(() => this.#updateActiveCategory(), {
        root: null,
        rootMargin: `-${offset}px 0px 0px 0px`,
        threshold: 0,
      });
    }

    #bindActiveCategoryObserver() {
      if (this.#isObserverBound) return;

      const elQuestions = this.closest('.faqs-template--content')?.querySelector('.faqs-template--questions');
      this.#sections = elQuestions
        ? [...elQuestions.querySelectorAll('.faqs-template--category-section[data-faq-category]')]
        : [];
      this.#links = [...this.querySelectorAll('.faqs-template--categories-link[data-faq-category]')];

      if (!this.#sections.length || !this.#links.length) {
        requestAnimationFrame(() => this.#bindActiveCategoryObserver());
        return;
      }

      this.#isObserverBound = true;

      for (const link of this.#links) {
        link.addEventListener('click', this.#handleLinkClick);
      }

      this.#categoryObserver = this.#createCategoryObserver();

      for (const section of this.#sections) {
        const elHeading = section.querySelector('.faqs-template--category-heading');
        if (elHeading) {
          this.#categoryObserver.observe(elHeading);
        }
      }

      this.#handleResize = () => {
        this.#categoryObserver?.disconnect();
        this.#categoryObserver = this.#createCategoryObserver();

        for (const section of this.#sections) {
          const elHeading = section.querySelector('.faqs-template--category-heading');
          if (elHeading) {
            this.#categoryObserver.observe(elHeading);
          }
        }

        this.#updateActiveCategory();
      };

      window.addEventListener('resize', this.#handleResize);
      this.#updateActiveCategory();
    }
  }

  customElements.define('faqs-categories', FaqsCategories);
}
